import { useState } from 'react';
import { ScrollReveal } from '@/components/effects/ScrollReveal';
import {
  ShieldAlert,
  Globe,
  Search,
  Clock,
  Lock,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  ArrowRight,
  Loader2,
} from 'lucide-react';

// Same resolution logic as the rest of the app — empty string means same-origin.
const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

// Loose client-side guards — the backend validates strictly and is the source of truth.
const BRAND_RE = /^[A-Za-z0-9.-]{3,50}$/;
const DOMAIN_RE = /^[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const ACTIVE_GUARD_URL = '/watch';

// ── Shared bits ──────────────────────────────────────────────────────────────

function CodeChip({ children }) {
  return (
    <span
      className="font-mono text-sm px-2.5 py-1 rounded-[3px] break-all"
      style={{ background: 'rgba(139,92,246,0.10)', border: '1px solid rgba(139,92,246,0.20)', color: 'var(--white)' }}
    >
      {children}
    </span>
  );
}

function PurpleButton({ href, children, full = false }) {
  return (
    <a
      href={href}
      className={`btn-shimmer inline-flex items-center justify-center gap-2 font-body font-medium h-12 px-6 rounded-[3px] whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-offset-2 ${full ? 'w-full' : ''}`}
      style={{ background: 'var(--a)', color: '#fff', '--tw-ring-color': 'var(--a)', '--tw-ring-offset-color': 'var(--l1)' }}
    >
      {children}
    </a>
  );
}

function ActiveGuardUpsell({ eyebrow, body, buttonLabel = 'Start 7-day trial' }) {
  return (
    <div className="rounded-[6px] p-6 sm:p-7 mt-5" style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.20)' }}>
      <div className="font-body text-[11px] font-medium uppercase tracking-[0.16em] mb-2.5" style={{ color: 'var(--a-bright)' }}>
        {eyebrow}
      </div>
      <p className="font-body text-sm sm:text-[15px] mb-5" style={{ color: 'var(--white)', lineHeight: '1.65' }}>
        {body}
      </p>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <PurpleButton href={ACTIVE_GUARD_URL}>
          {buttonLabel} <ArrowRight size={15} />
        </PurpleButton>
        <span className="font-body text-xs" style={{ color: 'var(--ts)' }}>
          From £49/month · cancel anytime
        </span>
      </div>
    </div>
  );
}

function StatusBanner({ tone, icon: Icon, title, subtitle }) {
  const palette = {
    danger: { bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.30)', color: 'var(--danger)' },
    warn: { bg: 'rgba(251,176,64,0.08)', border: 'rgba(251,176,64,0.30)', color: 'var(--warn)' },
    ok: { bg: 'rgba(74,222,128,0.08)', border: 'rgba(74,222,128,0.28)', color: 'var(--ok)' },
  }[tone];
  return (
    <div className="flex items-start gap-3.5 rounded-[5px] px-4 py-4 sm:px-5" style={{ background: palette.bg, border: `1px solid ${palette.border}` }}>
      <Icon size={22} style={{ color: palette.color, flexShrink: 0, marginTop: 2 }} />
      <div>
        <p className="font-body font-medium text-base sm:text-lg" style={{ color: 'var(--white)' }}>{title}</p>
        <p className="font-body text-sm mt-1" style={{ color: 'var(--tm)', lineHeight: '1.6' }}>{subtitle}</p>
      </div>
    </div>
  );
}

function LoadingBlock({ label }) {
  return (
    <div data-testid="checks-loading" className="flex items-center justify-center gap-3 py-12" style={{ color: 'var(--tm)' }}>
      <Loader2 size={18} className="animate-spin" style={{ color: 'var(--a)' }} />
      <span className="font-body text-sm">{label}</span>
    </div>
  );
}

function ErrorBlock({ message, onRetry }) {
  return (
    <div className="rounded-[5px] px-4 py-5 text-center" style={{ background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.22)' }}>
      <p className="font-body text-sm mb-3" style={{ color: 'var(--white)' }}>{message}</p>
      <button
        onClick={onRetry}
        className="font-body text-sm px-4 h-10 rounded-[3px] focus:outline-none focus:ring-2"
        style={{ background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.30)', color: 'var(--white)', '--tw-ring-color': 'var(--a)' }}
      >
        Try again
      </button>
    </div>
  );
}

const FIELD_STYLE = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid rgba(139,92,246,0.18)',
  color: 'var(--white)',
};

function Field({ value, onChange, placeholder, onSubmit, disabled, ariaLabel, testId }) {
  return (
    <input
      data-testid={testId}
      type="text"
      autoComplete="off"
      autoCapitalize="off"
      spellCheck={false}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(); }}
      disabled={disabled}
      aria-label={ariaLabel}
      className="font-body text-sm h-12 rounded-[3px] flex-1 px-3.5 min-w-0 focus:outline-none placeholder:text-white/40"
      style={FIELD_STYLE}
      onFocus={(e) => { e.target.style.borderColor = 'var(--a)'; e.target.style.boxShadow = '0 0 15px rgba(139,92,246,0.08)'; }}
      onBlur={(e) => { e.target.style.borderColor = 'rgba(139,92,246,0.18)'; e.target.style.boxShadow = 'none'; }}
    />
  );
}

function CheckButton({ onClick, disabled, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className="btn-shimmer font-body font-medium px-6 h-12 rounded-[3px] whitespace-nowrap inline-flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2"
      style={{ background: 'var(--a)', color: '#fff', opacity: disabled ? 0.7 : 1, '--tw-ring-color': 'var(--a)', '--tw-ring-offset-color': 'var(--l1)' }}
    >
      Check now
    </button>
  );
}

function Label({ children }) {
  return <label className="font-body text-sm font-medium block mb-2" style={{ color: 'var(--white)' }}>{children}</label>;
}

function WhatYouCanDo({ items, title = 'What you can do now' }) {
  return (
    <div className="rounded-[5px] p-5 mt-5" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="font-body text-[11px] font-medium uppercase tracking-[0.16em] mb-3" style={{ color: 'var(--ts)' }}>{title}</div>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2.5 font-body text-sm" style={{ color: 'var(--tm)', lineHeight: '1.6' }}>
            <ArrowRight size={14} style={{ color: 'var(--a)', flexShrink: 0, marginTop: 4 }} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Brand Impersonation tab ──────────────────────────────────────────────────

const RISK_META = {
  high: { color: 'var(--danger)', label: 'HIGH RISK', bg: 'rgba(248,113,113,0.14)' },
  medium: { color: 'var(--warn)', label: 'MEDIUM', bg: 'rgba(251,176,64,0.14)' },
  low: { color: 'var(--ts)', label: 'LOW', bg: 'rgba(240,237,248,0.08)' },
};

const BRAND_DETECTS = [
  'Homoglyph swaps (0 for o, l for i)',
  'Typosquats and added letters',
  'Suspicious phrase prefixes/suffixes',
];

function LookalikeRow({ r }) {
  const meta = RISK_META[r.risk] || RISK_META.low;
  return (
    <div
      className="flex items-center gap-3 rounded-[4px] pl-3.5 pr-3 py-3"
      style={{ background: 'rgba(255,255,255,0.02)', borderLeft: `3px solid ${meta.color}`, border: '1px solid rgba(255,255,255,0.06)', borderLeftWidth: 3, borderLeftColor: meta.color }}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className="font-mono text-sm break-all" style={{ color: 'var(--white)' }}>{r.domain}</span>
          <span className="font-body text-[10px] font-medium tracking-[0.08em] px-2 py-0.5 rounded-full" style={{ color: meta.color, background: meta.bg }}>
            {meta.label}
          </span>
        </div>
        <p className="font-body text-xs mt-1" style={{ color: 'var(--tm)' }}>
          {r.reason} · Registered {r.registered_days_ago} day{r.registered_days_ago === 1 ? '' : 's'} ago · {r.has_active_ssl ? 'Active' : 'No'} SSL cert
        </p>
      </div>
      <ExternalLink size={15} style={{ color: 'var(--ts)', flexShrink: 0 }} aria-hidden />
    </div>
  );
}

function BrandTab() {
  const [input, setInput] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | result | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [queried, setQueried] = useState('');

  async function submit() {
    const clean = input.trim();
    if (!BRAND_RE.test(clean.replace(/^https?:\/\//i, '').split('/')[0])) {
      setError('Enter a valid business name or domain (3–50 characters).');
      setStatus('error');
      return;
    }
    setStatus('loading');
    setError('');
    setQueried(clean);
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/brand-watch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand: clean }),
      });
      if (res.status === 429) throw new Error('Too many checks. Please wait a minute and try again.');
      if (!res.ok) throw new Error("Couldn't complete the check. Please try again.");
      setResult(await res.json());
      setStatus('result');
    } catch (err) {
      setError(err.message || "Couldn't complete the check. Please try again.");
      setStatus('error');
    }
  }

  if (status === 'loading') return <LoadingBlock label="Scanning certificate transparency logs…" />;
  if (status === 'error') return <ErrorBlock message={error} onRetry={() => setStatus('idle')} />;

  if (status === 'result' && result) {
    const found = result.results.length > 0;
    return (
      <div data-testid="brand-result">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2 font-body text-sm" style={{ color: 'var(--tm)' }}>
            Results for <CodeChip>{result.brand_input || queried}</CodeChip>
          </div>
          <span className="font-body text-xs" style={{ color: 'var(--ts)' }}>last {result.lookback_days} days</span>
        </div>

        {found ? (
          <StatusBanner
            tone="danger"
            icon={AlertTriangle}
            title={`${result.total_found} suspicious domain${result.total_found === 1 ? '' : 's'} found`}
            subtitle="Recently registered domains that mimic your brand. Investigate immediately."
          />
        ) : (
          <StatusBanner
            tone="ok"
            icon={CheckCircle2}
            title="No suspicious lookalikes detected"
            subtitle="We didn't find any recently-registered domains mimicking your brand in the last 90 days."
          />
        )}

        {found && (
          <div className="space-y-2.5 mt-4">
            {result.results.map((r) => <LookalikeRow key={r.domain} r={r} />)}
          </div>
        )}

        {found && result.total_found > result.results.length && (
          <p className="font-body text-xs mt-3" style={{ color: 'var(--ts)', lineHeight: '1.6' }}>
            Showing top {result.results.length} of {result.total_found} matches. Acodia Watch surfaces all results plus continuous monitoring.
          </p>
        )}

        <WhatYouCanDo
          items={found
            ? [
                'Submit takedown requests to the registrars of high-risk domains',
                'Warn staff and customers about the impersonator domains',
                'Set Google Alerts for your brand name',
              ]
            : [
                'Stay vigilant — new impersonator domains can appear at any time',
                'Set Google Alerts for your brand name',
                'Register the most obvious lookalike TLDs before someone else does',
              ]}
        />

        <ActiveGuardUpsell
          eyebrow="Continuous monitoring with Acodia Watch"
          body={found
            ? 'Get alerted within 1 hour when a new lookalike domain is registered. Acodia Watch monitors certificate transparency logs 24/7 — no checking back manually.'
            : 'Continuous monitoring catches the moment something IS registered. Acodia Watch monitors certificate transparency logs 24/7 and alerts you within the hour.'}
        />

        <button
          onClick={() => { setStatus('idle'); setResult(null); }}
          className="font-body text-sm mt-5 focus:outline-none"
          style={{ color: 'var(--a)' }}
        >
          ← Check another brand
        </button>
      </div>
    );
  }

  // idle
  return (
    <div data-testid="brand-idle">
      <Label>Your business name or domain</Label>
      <div className="flex flex-col sm:flex-row gap-3">
        <Field testId="brand-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="acodia" onSubmit={submit} ariaLabel="Business name or domain" />
        <CheckButton testId="brand-submit" onClick={submit} />
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4">
        {[
          { icon: Lock, label: 'No login required' },
          { icon: Search, label: 'Scans last 90 days' },
          { icon: Clock, label: 'Results in ~5 seconds' },
        ].map(({ icon: Icon, label }) => (
          <span key={label} className="inline-flex items-center gap-1.5 font-body text-xs" style={{ color: 'var(--tm)' }}>
            <Icon size={13} style={{ color: 'var(--a)' }} /> {label}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mt-5">
        {BRAND_DETECTS.map((d) => (
          <div key={d} className="rounded-[4px] px-3 py-2.5 font-body text-xs" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', color: 'var(--tm)', lineHeight: '1.5' }}>
            <span style={{ color: 'var(--ts)' }}>Detects: </span>{d.replace(/^Detects?:\s*/, '')}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Domain Security tab ──────────────────────────────────────────────────────

const CATEGORY_ORDER = [
  { key: 'email_spoofing', name: 'Email spoofing' },
  { key: 'email_authentication', name: 'Email authentication' },
  { key: 'reputation', name: 'Reputation' },
  { key: 'certificate_health', name: 'Certificate health' },
];

function CategoryCard({ name, cat }) {
  const ok = cat.status === 'ok';
  const Icon = ok ? CheckCircle2 : AlertCircle;
  const color = ok ? 'var(--ok)' : cat.status === 'warning' ? 'var(--warn)' : 'var(--danger)';
  return (
    <div className="flex items-start gap-3 rounded-[5px] p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <Icon size={20} style={{ color, flexShrink: 0, marginTop: 1 }} />
      <div>
        <p className="font-body font-medium text-sm" style={{ color: 'var(--white)' }}>{name}</p>
        <p className="font-body text-xs mt-0.5" style={{ color: 'var(--tm)' }}>{cat.label}</p>
      </div>
    </div>
  );
}

function DomainTab() {
  const [input, setInput] = useState('');
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  async function submit() {
    const clean = input.trim().replace(/^https?:\/\//i, '').split('/')[0];
    if (!DOMAIN_RE.test(clean)) {
      setError('Enter a valid domain, e.g. example.com.');
      setStatus('error');
      return;
    }
    setStatus('loading');
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/domain-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: clean }),
      });
      if (res.status === 429) throw new Error('Too many checks. Please wait a minute and try again.');
      if (!res.ok) throw new Error("Couldn't complete the check. Please try again.");
      setResult(await res.json());
      setStatus('result');
    } catch (err) {
      setError(err.message || "Couldn't complete the check. Please try again.");
      setStatus('error');
    }
  }

  if (status === 'loading') return <LoadingBlock label="Checking DNS, blocklists and certificates…" />;
  if (status === 'error') return <ErrorBlock message={error} onRetry={() => setStatus('idle')} />;

  if (status === 'result' && result) {
    const cats = result.categories;
    const issueCount = CATEGORY_ORDER.filter((c) => cats[c.key]?.status !== 'ok').length;
    const banner = {
      issues: { tone: 'danger', icon: AlertTriangle, title: 'Action needed', subtitle: `${issueCount} of 4 categories have issues.` },
      needs_attention: { tone: 'warn', icon: AlertTriangle, title: 'Needs attention', subtitle: `Issues found in ${issueCount} of 4 categories.` },
      healthy: { tone: 'ok', icon: CheckCircle2, title: 'Looks healthy', subtitle: 'All 4 categories check out.' },
    }[result.overall_status] || { tone: 'warn', icon: AlertTriangle, title: 'Needs attention', subtitle: `Issues found in ${issueCount} of 4 categories.` };

    return (
      <div data-testid="domain-result">
        <div className="flex items-center gap-2 font-body text-sm mb-4" style={{ color: 'var(--tm)' }}>
          Snapshot for <CodeChip>{result.domain}</CodeChip>
        </div>

        <StatusBanner tone={banner.tone} icon={banner.icon} title={banner.title} subtitle={banner.subtitle} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-4">
          {CATEGORY_ORDER.map((c) => cats[c.key] && <CategoryCard key={c.key} name={c.name} cat={cats[c.key]} />)}
        </div>

        <p className="font-body text-xs mt-4" style={{ color: 'var(--ts)', lineHeight: '1.6' }}>
          This is a one-time snapshot. We don't store the result and can't tell if these issues are new, worsening, or recently introduced.
        </p>

        <ActiveGuardUpsell
          eyebrow="See exactly what's wrong + fix instructions"
          body="Acodia Watch shows you the specific findings, severity levels, provider-specific fix snippets (Nginx, Apache, Cloudflare), and continuously monitors so you're alerted the moment something changes."
        />

        <button
          onClick={() => { setStatus('idle'); setResult(null); }}
          className="font-body text-sm mt-5 focus:outline-none"
          style={{ color: 'var(--a)' }}
        >
          ← Check another domain
        </button>
      </div>
    );
  }

  // idle
  return (
    <div data-testid="domain-idle">
      <Label>Your domain</Label>
      <div className="flex flex-col sm:flex-row gap-3">
        <Field testId="domain-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="example.com" onSubmit={submit} ariaLabel="Domain" />
        <CheckButton testId="domain-submit" onClick={submit} />
      </div>
      <p className="font-body text-xs mt-4" style={{ color: 'var(--tm)' }}>
        We query public DNS, blocklists, and certificate transparency logs.
      </p>
    </div>
  );
}

// ── Outer component ──────────────────────────────────────────────────────────

const TABS = [
  { key: 'brand', label: 'Brand impersonation', icon: ShieldAlert },
  { key: 'domain', label: 'Domain security', icon: Globe },
];

export function FreeSecurityChecks({ hideHeading = false }) {
  const [tab, setTab] = useState('brand');

  return (
    <section id="scanner" data-testid="free-security-checks" className="py-20 sm:py-28" style={{ background: 'var(--bg)' }}>
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          {!hideHeading && (
            <div className="text-center max-w-3xl mx-auto mb-12">
              <div className="font-body text-xs uppercase tracking-[0.22em] mb-3" style={{ color: 'var(--a)' }}>
                Free security checks
              </div>
              <h2 className="font-display font-extrabold tracking-[-0.03em] mb-4" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.8rem)', color: 'var(--white)' }}>
                See your security exposure in seconds
              </h2>
              <p className="font-body text-base sm:text-lg" style={{ color: 'var(--tm)', lineHeight: '1.7' }}>
                Two free tools for businesses — uses only public data, no scanning involved.
              </p>
            </div>
          )}

          <div
            className="rounded-[8px] p-5 sm:p-7 max-w-[900px] mx-auto"
            style={{ background: 'var(--l1)', border: '1px solid var(--b2)', boxShadow: '0 30px 80px rgba(0,0,0,0.35)' }}
          >
            {/* Tab strip — segmented control */}
            <div className="flex gap-1.5 p-1 rounded-[6px] mb-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              {TABS.map(({ key, label, icon: Icon }) => {
                const active = tab === key;
                return (
                  <button
                    key={key}
                    data-testid={`tab-${key}`}
                    onClick={() => setTab(key)}
                    className="flex-1 inline-flex items-center justify-center gap-2 font-body text-sm font-medium h-11 rounded-[4px] transition-colors focus:outline-none focus:ring-2"
                    style={{
                      background: active ? 'rgba(139,92,246,0.14)' : 'transparent',
                      border: active ? '1px solid var(--a-border)' : '1px solid transparent',
                      color: active ? 'var(--a-bright)' : 'var(--tm)',
                      '--tw-ring-color': 'var(--a)',
                    }}
                  >
                    <Icon size={16} /> <span className="whitespace-nowrap">{label}</span>
                  </button>
                );
              })}
            </div>

            {/* Panel — content sits directly on the dark card surface */}
            <div className="pt-1">
              {tab === 'brand' ? <BrandTab /> : <DomainTab />}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

export default FreeSecurityChecks;
